import React from 'react';
import styled from 'styled-components';
import { Button, Box, TextField, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { createRFDiffusionJob } from '@/api/jobs';
import { RFDiffusionForm } from '@/components/ModelForms';
import { useAwsSettings } from '@/hooks/useAwsSettings';
import { RFDiffusionParams } from '@/types/colabfold';
import JobFormHeader from '@/components/JobFormHeader';
import { ModelType } from '@/types/colabfold';

// Interface representing the form values expected by RFDiffusionForm
interface FormValues {
  mode?: string;
  input_pdb_file?: File;
  input_pdb?: string;
  sequence?: string;
  contigs?: string;
  num_designs?: number;
  chain_length?: number;
  symmetry?: string;
  use_multimer?: boolean;
  model_type?: string;
  noise_scale?: number;
  hotspot_residues?: string;
  [key: string]: unknown;
}

const RFDiffusionTool = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = React.useState<RFDiffusionParams>({
    jobname: `RFDiffusion-${new Date().toISOString().split('T')[0]}`,
    query_sequence: '',
    sequence: '',
    num_designs: 1,
    num_diffusion_steps: 100,
    noise_scale: 0.5,
    motif_scaffold: '',
    symmetry: { type: 'cyclic', order: 1 },
    symmetric_handling: 'none',
    contigs: '',
    length: 100,
    temperature: 0.5,
    seed: 42,
    model_type: 'default',
    design_mode: 'general',
    use_multimer: false,
    folder: location.state?.folderId || null
  });
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const { isComplete: hasCompleteAwsSettings, loading: awsSettingsLoading } = useAwsSettings();

  // Adapter for form values - convert RFDiffusionParams to FormValues
  const [formValues, setFormValues] = React.useState<FormValues>({});

  // Update formValues whenever formData changes
  React.useEffect(() => {
    // Convert from RFDiffusionParams to FormValues
    const symmetryString = typeof formData.symmetry === 'object' && formData.symmetry !== null
      ? `${formData.symmetry.type === 'cyclic' ? 'C' : 'D'}${formData.symmetry.order}`
      : 'none';

    setFormValues({
      mode: formData.design_mode || 'general',
      input_pdb: formData.input_pdb || '',
      sequence: formData.sequence || '',
      contigs: formData.contigs || '',
      num_designs: formData.num_designs || 1,
      chain_length: formData.length || 100,
      symmetry: symmetryString,
      use_multimer: formData.use_multimer || false,
      model_type: formData.model_type || 'default',
      noise_scale: formData.noise_scale || 0.5,
      hotspot_residues: formData.hotspots || ''
    });
  }, [formData]);

  // Add a separate handler for file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      console.log('File selected:', file.name, 'Size:', file.size, 'bytes');
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          console.log('File content loaded:', 
            `Length: ${content.length}`, 
            `First 50 chars: ${content.substring(0, 50).replace(/\n/g, '\\n')}`);
          
          // Update formData with the PDB content
          setFormData({
            ...formData,
            input_pdb: content,
            sequence: content  // For backward compatibility
          });
          
          // Also update formValues for the new component
          setFormValues({
            ...formValues,
            input_pdb: content,
            input_pdb_file: file
          });
          
          console.log('Updated form data with PDB content');
        } else {
          console.error('Failed to read file content');
        }
      };
      
      reader.onerror = (e) => {
        console.error('Error reading file:', e);
        setError('Failed to read PDB file. Please try again with a different file.');
      };
      
      reader.readAsText(file);
    }
  };

  // Update formData whenever formValues changes
  const handleFormValuesChange = (values: FormValues) => {
    // Convert from FormValues to RFDiffusionParams
    const updatedFormData = { ...formData };
    
    // Mode mapping
    if (values.mode !== formData.design_mode) {
      updatedFormData.design_mode = values.mode as 'general' | 'binder' | 'scaffold';
      // Set binder_design flag based on mode
      updatedFormData.binder_design = values.mode === 'binder';
    }
    
    // Handle PDB content
    if (values.input_pdb !== formData.input_pdb) {
      updatedFormData.input_pdb = values.input_pdb || '';
    }
    
    // Handle sequence
    if (values.sequence !== formData.sequence) {
      updatedFormData.sequence = values.sequence || '';
    }
    
    // Handle contigs
    if (values.contigs !== formData.contigs) {
      updatedFormData.contigs = values.contigs || '';
    }
    
    // Handle number of designs
    if (values.num_designs !== formData.num_designs) {
      updatedFormData.num_designs = values.num_designs || 1;
    }
    
    // Handle chain length
    if (values.chain_length !== formData.length) {
      updatedFormData.length = values.chain_length || 100;
    }
    
    // Handle symmetry
    if (values.symmetry !== undefined && values.symmetry !== 'none') {
      const match = values.symmetry.match(/([CD])(\d+)/);
      if (match) {
        const [, type, order] = match;
        updatedFormData.symmetry = {
          type: type === 'C' ? 'cyclic' : 'dihedral',
          order: parseInt(order, 10)
        };
      }
    }
    
    // Handle hotspots/hotspot_residues
    if (values.hotspot_residues !== formData.hotspots) {
      updatedFormData.hotspots = values.hotspot_residues;
    }
    
    // Handle model_type
    if (values.model_type !== formData.model_type) {
      updatedFormData.model_type = values.model_type as 'default' | 'beta';
    }
    
    // Handle noise_scale
    if (values.noise_scale !== formData.noise_scale) {
      updatedFormData.noise_scale = values.noise_scale || 1.0;
    }
    
    // Handle use_multimer
    if (values.use_multimer !== undefined) {
      updatedFormData.use_multimer = values.use_multimer;
    }
    
    // Update form data
    setFormData(updatedFormData);
  };

  // Check if contigs are provided but no input PDB
  // Consider both input_pdb and sequence fields since the PDB might be in either
  const hasContigsButNoPDB = formData.contigs && !(formData.input_pdb || (formData.sequence && formData.sequence.trim().startsWith('MODEL')));

  // Validate hotspots format if provided
  const validateHotspots = (hotspots?: string): boolean => {
    if (!hotspots || hotspots === '') return true; // Empty is valid
    
    // Format should be chain-letter followed by residue number, separated by commas
    // Valid examples: A30,A33,A34 or A50,B30,C45 
    const hotspotPattern = /^[A-Z][0-9]+(?:,[A-Z][0-9]+)*$/;
    return hotspotPattern.test(hotspots);
  };

  const hasInvalidHotspots = Boolean(formData.hotspots) && !validateHotspots(formData.hotspots);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await createRFDiffusionJob(formData);
      navigate('/jobs'); // Navigate back to the all jobs list after submission
    } catch (error) {
      console.error('Error creating RF Diffusion job:', error);
      setError('Failed to submit job. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/jobs'); // Navigate back to the all jobs list
  };

  return (
    <Container>
      <Box p={3}>
        <JobFormHeader modelType={ModelType.RFDIFFUSION} />
        <Box mb={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {!hasCompleteAwsSettings && !awsSettingsLoading && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your AWS settings are incomplete. Please complete your AWS settings before submitting a job.
            </Alert>
          )}
          
          {hasContigsButNoPDB && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              When contigs are provided, an input PDB file is required. Please upload a PDB file.
            </Alert>
          )}
          
          {hasInvalidHotspots && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Invalid hotspots format. Please enter hotspots in the format chain-letter followed by residue number, separated by commas.
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Job Name"
            value={formData.jobname}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({...formData, jobname: e.target.value})}
            required
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="PDB File"
            type="file"
            InputLabelProps={{ shrink: true }}
            onChange={handleFileUpload}
            error={!!formValues.input_pdb_file}
            helperText={formValues.input_pdb_file ? "PDB file uploaded successfully" : "Upload a PDB file containing the template structure"}
            sx={{ mb: 3 }}
          />
          <RFDiffusionForm 
            params={formValues}
            onChange={handleFormValuesChange}
          />
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button 
              variant="outlined" 
              onClick={handleCancel} 
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmit}
              disabled={!formData.jobname || hasContigsButNoPDB || !hasCompleteAwsSettings || submitting || awsSettingsLoading || hasInvalidHotspots}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
`;

export default RFDiffusionTool; 